// src/pages/Sports.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, Trophy, Users, MapPin, Award } from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { SportCard } from '@/components/SportCard';
import { sports } from '@/data/mock/sports';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const Sports = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Filter sports based on search and category
  const filteredSports = sports.filter(sport => {
    const matchesSearch = sport.name[isRTL ? 'ar' : 'en']
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || sport.id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Categories for filtering
  const categories = [
    { id: 'all', label: { en: 'All Sports', ar: 'جميع الرياضات' }, icon: Trophy },
    { id: 'team', label: { en: 'Team Sports', ar: 'الرياضات الجماعية' }, icon: Users },
    { id: 'individual', label: { en: 'Individual', ar: 'رياضات فردية' }, icon: Award },
  ];

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('sports.title')}
        subtitle={t('sports.subtitle')}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.sports') }
        ]}
      />

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            {[
              {
                icon: Trophy,
                value: '8',
                label: { en: 'Training Programs', ar: 'برنامج تدريبي' }
              },
              {
                icon: Users,
                value: '50',
                label: { en: 'Staff Members', ar: 'طاقم العمل' }
              },
              {
                icon: MapPin,
                value: '5',
                label: { en: 'Branches', ar: 'فرع' }
              },
              {
                icon: Award,
                value: '20',
                label: { en: 'Championships & Titles', ar: 'بطولة ولقب' }
              },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="p-6 rounded-2xl bg-card/80 backdrop-blur-xl border border-border/50 hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <stat.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            {/* Search Bar */}
            <div className="relative mb-8">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? "ابحث عن رياضة..." : "Search for a sport..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-12 text-right' : 'pl-12'} h-14 text-lg rounded-full border-2 focus:border-primary/50`}
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  size="lg"
                  onClick={() => setSelectedCategory(category.id)}
                  className="rounded-full px-6 group"
                >
                  <category.icon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {category.label[isRTL ? 'ar' : 'en']}
                </Button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sports Grid */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          {/* Results Count */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-muted-foreground">
              {isRTL 
                ? `تم العثور على ${filteredSports.length} رياضة`
                : `Found ${filteredSports.length} sports`
              }
            </p>
          </motion.div>

          {/* Sports Grid */}
          {filteredSports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredSports.map((sport, idx) => (
                <SportCard
                  key={sport.id}
                  slug={sport.slug}
                  name={sport.name[isRTL ? 'ar' : 'en']}
                  description={sport.description[isRTL ? 'ar' : 'en']}
                  image={sport.image}
                  coaches={sport.coaches}
                  schedule={sport.schedule[isRTL ? 'ar' : 'en']}
                  delay={idx * 0.1}
                />
              ))}
            </div>
          ) : (
            // No Results
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
                <Search className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL 
                  ? 'جرب البحث بكلمات مختلفة أو تصفح جميع الرياضات'
                  : 'Try different keywords or browse all sports'
                }
              </p>
              <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                {isRTL ? 'إعادة تعيين البحث' : 'Reset Search'}
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-surface-1 relative overflow-hidden">
        {/* Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Trophy className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'ابدأ رحلتك الرياضية' : 'Start Your Sports Journey'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'جاهز للانضمام؟' : 'Ready to Join?'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'احجز جلستك التجريبية المجانية اليوم واكتشف برامجنا المتميزة'
                : 'Book your free trial session today and discover our premium programs'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
                {isRTL ? 'احجز الآن' : 'Book Now'}
              </Button>
              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sports;
